/**
 * Thin host transport — posts into the worklet/direct kernel.
 * `setInstrument` accepts a full Instrument or a legacy SynthPreset.
 */
import type {
  FxNode,
  Instrument,
  InstrumentSlot,
  KernelMessage,
  SamplerRuntimeSample,
  Sf2RuntimeBank,
  SlotMix,
  SynthPreset,
  VoiceNoteFxParams,
  WavetableRuntimeTable,
} from "../types";
import { synthInstrument } from "../kernel/drumKit";
import { cloneSf2BankForTransfer } from "../sf2/loadBank";
import { cloneWavetableForTransfer } from "../wavetable/loadTable";
import { cloneSampleForTransfer } from "../sampler/loadSample";

export type TransportState = {
  playing: boolean;
  bpm: number;
  /** AudioContext time when play began; 0 if stopped */
  startTime: number;
};

export type PostKernelMessage = (
  message: KernelMessage,
  transfer?: Transferable[],
) => void;

function asInstrument(instrumentOrPreset: Instrument | SynthPreset): Instrument {
  if (instrumentOrPreset && typeof instrumentOrPreset === "object" && "kind" in instrumentOrPreset) {
    return instrumentOrPreset;
  }
  return synthInstrument(instrumentOrPreset as SynthPreset);
}

/**
 * Host-side transport — owns BPM / play state and posts into the worklet kernel.
 */
export class AudioTransport {
  private state: TransportState = { playing: false, bpm: 120, startTime: 0 };

  constructor(
    private readonly post: PostKernelMessage,
    private readonly getAudioTime: () => number,
  ) {}

  getState(): TransportState {
    return { ...this.state };
  }

  get bpm(): number {
    return this.state.bpm;
  }

  setBpm(bpm: number): void {
    const next = Math.max(20, Math.min(400, bpm));
    this.state = { ...this.state, bpm: next };
    this.post({ type: "setBpm", bpm: next });
  }

  play(): void {
    const startTime = this.getAudioTime();
    this.state = { ...this.state, playing: true, startTime };
    this.post({ type: "transport", playing: true, startTime });
  }

  stop(): void {
    this.state = { ...this.state, playing: false, startTime: 0 };
    this.post({ type: "transport", playing: false, startTime: 0 });
  }

  noteOn(
    slot: InstrumentSlot,
    note: number,
    velocity = 1,
    time = 0,
    noteFx?: VoiceNoteFxParams,
  ): void {
    this.post({ type: "noteOn", slot, note, velocity, time, noteFx });
  }

  noteOff(slot: InstrumentSlot, note?: number, time = 0): void {
    this.post({ type: "noteOff", slot, note, time });
  }

  setNoteFx(
    slot: InstrumentSlot,
    noteFx: VoiceNoteFxParams,
    time = 0,
    velocity?: number,
  ): void {
    this.post({ type: "setNoteFx", slot, noteFx, time, velocity });
  }

  /** Accepts Instrument or legacy SynthPreset (wrapped as synth). */
  setInstrument(slot: InstrumentSlot, instrumentOrPreset: Instrument | SynthPreset): void {
    this.post({ type: "setInstrument", slot, instrument: asInstrument(instrumentOrPreset) });
  }

  /** Push a parsed SF2 runtime bank into the kernel (transferable sample buffers). */
  loadSf2Bank(fontId: string, bank: Sf2RuntimeBank): void {
    const cloned = cloneSf2BankForTransfer(bank);
    this.post({ type: "loadSf2Bank", fontId, bank: cloned.bank }, cloned.transfer);
  }

  /** Push a decoded wavetable into the kernel. */
  loadWavetable(table: WavetableRuntimeTable): void {
    const cloned = cloneWavetableForTransfer(table);
    this.post({ type: "loadWavetable", table: cloned.table }, cloned.transfer);
  }

  /** Push decoded mono PCM into the kernel sample map. */
  loadSample(sample: SamplerRuntimeSample): void {
    const cloned = cloneSampleForTransfer(sample);
    this.post({ type: "loadSample", sample: cloned.sample }, cloned.transfer);
  }

  setSlotMix(slot: InstrumentSlot, mix: SlotMix): void {
    this.post({ type: "setSlotMix", slot, mix });
  }

  setSlotMixes(mixes: SlotMix[]): void {
    this.post({ type: "setSlotMixes", mixes });
  }

  setFxStack(effects: FxNode[]): void {
    this.post({ type: "setFxStack", effects });
  }

  setFx(index: number, patch: Partial<FxNode>): void {
    this.post({ type: "setFx", index, patch });
  }

  setFxBypass(bypass: boolean): void {
    this.post({ type: "setFxBypass", bypass });
  }

  clearFx(): void {
    this.post({ type: "clearFx" });
  }

  setSlotFxStack(slot: InstrumentSlot, effects: FxNode[]): void {
    this.post({ type: "setSlotFxStack", slot, effects });
  }

  setSlotFxStacks(stacks: FxNode[][]): void {
    this.post({ type: "setSlotFxStacks", stacks });
  }

  clearSlotFx(slot?: InstrumentSlot): void {
    this.post({ type: "clearSlotFx", slot });
  }

  allNotesOff(): void {
    this.post({ type: "allNotesOff" });
  }

  /** Drop kernel pending events with time > afterTime (live schedule rebuild). */
  cancelPending(afterTime: number): void {
    this.post({ type: "cancelPending", afterTime });
  }
}
